import { Close } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpOutlinedIcon from '@mui/icons-material/ArrowDropUpOutlined';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import { Badge, Box, Button, InputAdornment, Popover, TextField, Typography } from '@mui/material';
import type { TreeItemContentProps, TreeItemProps } from '@mui/x-tree-view';
import { TreeItem, TreeView, useTreeItem } from '@mui/x-tree-view';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { LocalStorageService } from '../../services/local-storage';
import { fuelUserAccessSliceSelector } from '../../store/slices/fuelUserAccess';
import { treeViewUtils } from '../../utils';

// declare module '@mui/lab' {
//   interface TreeItemContentProps {
//     count: number;
//   }
// }
const CustomContent = React.forwardRef(function CustomContent(props: TreeItemContentProps, ref) {
  const { className, classes, label, nodeId, icon: iconProp, expansionIcon, displayIcon, count } = props;

  const { disabled, expanded, selected, focused, handleExpansion, handleSelection, preventSelection } = useTreeItem(nodeId);

  const icon = iconProp || expansionIcon || displayIcon;

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    preventSelection(event);
  };

  const handleExpansionClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    handleExpansion(event);
  };

  const handleSelectionClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    handleSelection(event);
  };

  return (
    <div
      className={clsx(className, classes.root, {
        [classes.expanded]: expanded,
        [classes.selected]: selected,
        [classes.focused]: focused,
        [classes.disabled]: disabled,
      })}
      onMouseDown={handleMouseDown}
      ref={ref as React.Ref<HTMLDivElement>}
    >
      <div className={classes.iconContainer} onClick={handleExpansionClick}>
        {icon}
      </div>
      <Typography onClick={handleSelectionClick} component="div" className={classes.label} sx={{ fontSize: '16px !important' }}>
        {label}
      </Typography>
      {count > 0 && (
        <Box color="inherit" sx={{ mr: 1 }}>
          <Badge badgeContent={count} color="success" />
        </Box>
      )}
    </div>
  );
});

function CustomTreeItem(props: TreeItemProps) {
  return <TreeItem ContentComponent={CustomContent} {...props} />;
}
const FuelTreeSelectDropDown = ({ edit, newData, label, selectedValue, onChange }) => {
  const { permissions, fuelUserAccessData, accessibleOUs } = useSelector(fuelUserAccessSliceSelector);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNodeLabel, setSelectedNodeLabel] = useState('');
  const [selectedNodeId, selSectedNodeId] = useState(selectedValue ? selectedValue.ouId : '');
  const [items, setItems] = useState(newData);
  const [expanded, setExpanded] = React.useState([]);
  const [searchText, setSearchText] = useState('');

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const renderTree = (nodes) => {
    // console.log('nodes', nodes)
    return (
      <CustomTreeItem
        key={nodes.ouId}
        nodeId={nodes.ouId}
        label={nodes.ouName}
        ContentProps={{
          count: nodes.count,
        }}
      >
        {Array.isArray(nodes.children) ? nodes.children.map((node) => renderTree(node)) : null}
      </CustomTreeItem>
    );
  };

  const onNodeSelect = (e, nodeId) => {
    selSectedNodeId(nodeId);
    setSelectedNodeLabel(e.target.innerText);
    onChange(nodeId);
  };
  const handleToggle = (event, nodeIds) => {
    let expandedTemp = expanded;
    expandedTemp = nodeIds;
    setExpanded(expandedTemp);
    LocalStorageService.setItem('expandedNodes', expandedTemp);
  };

  const onSearch = (value) => {
    const filter = value;
    setSearchText(filter);
    if (!filter) {
      setItems(() => newData);
      const expandedNodes = LocalStorageService.getItem('expandedNodes');
      if (expandedNodes) {
        // console.log('treeViewUtils.getIDsExpandFilter(defaultLoggedInOu)', treeViewUtils.getIDsExpandFilter(defaultLoggedInOu));
        setExpanded(expandedNodes);
      }
      return;
    }
    const filtered = treeViewUtils.filterBy(newData, filter);
    setItems(filtered);
    if (filtered && filtered.length > 0) {
      setExpanded(treeViewUtils.getIDsExpandFilter(filtered[0]));
    }
  };

  useEffect(() => {
    if (selectedValue) {
      setSelectedNodeLabel(selectedValue.ouName);
      selSectedNodeId(selectedValue ? selectedValue.ouId : '');
    }
  }, [selectedValue]);

  useEffect(() => {}, [items]);
  useEffect(() => {
    if (newData) {
      setItems(newData);
    }
    if (newData && selectedValue) {
      onSearch('');
    }
  }, [newData]);

  return (
    <>
      <Button
        variant="text"
        endIcon={anchorEl ? <ArrowDropUpOutlinedIcon className="dropdownIcon" /> : <ArrowDropDownIcon className="dropdownIcon" />}
        onClick={handleClick}
      >
        {selectedNodeLabel}
      </Button>
      <Popover
        id={id}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <TextField
          variant="outlined"
          required={false}
          placeholder="Search OU"
          name="ou"
          id="searchOu"
          fullWidth
          size="small"
          autoComplete="off"
          sx={{ padding: '5px' }}
          onChange={(e) => onSearch(e.target.value)}
          value={searchText}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Close onClick={() => onSearch('')} fontSize="small" className="clickable" />
              </InputAdornment>
            ),
          }}
        />
        <TreeView
          aria-label="icon expansion"
          defaultSelected={selectedNodeId}
          selected={selectedNodeId}
          expanded={expanded}
          onNodeToggle={handleToggle}
          defaultCollapseIcon={<ArrowDropDownIcon />}
          defaultExpandIcon={<ArrowRightIcon />}
          onNodeSelect={(e, nodeId) => onNodeSelect(e, nodeId)}
          sx={{
            minHeight: '50px',
            maxHeight: '400px',
            flexGrow: 1,
            // maxWidth: "400px",
            minWidth: '400px',
            overflowY: 'auto',
            marginTop: 1,
            marginBottom: 1,
          }}
        >
          {items && items.map((item, i) => renderTree(item))}
        </TreeView>
      </Popover>
    </>
  );
};

export default FuelTreeSelectDropDown;
